import { format } from "date-fns"
import { getDateFnsLocale } from "@/v2-common/utils/utils.date"
import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import MultiSelect from "@/v2-ui/multiSelect"
import DatePicker from "@/v2-ui/datePicker"
import Button from "@/v2-backoffice-client/components/button"
import Dot from "@/v2-backoffice-client/components/dot"
import type { ColumnFilterConfig } from "../table.types"
import DateFilter from "./dateFilter"
import type { DateFilterValue } from "./dateFilter"

type TableFilterButtonInputProps = {
  filter: ColumnFilterConfig
  value: unknown
  onChange(value: unknown): void
}

/**
 * Renders the appropriate filter input based on the filter type.
 */
function TableFilterButtonInput(props: TableFilterButtonInputProps) {
  const { filter, value, onChange } = props

  switch (filter.type) {
    case "text": {
      return (
        <Input
          type="text"
          size="xs"
          placeholder={filter.placeholder ?? "Filter..."}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || undefined)}
          className="ui-table-filter-input"
        />
      )
    }

    case "number": {
      return (
        <Input
          type="number"
          size="xs"
          placeholder={filter.placeholder ?? "Filter..."}
          value={(value as string) ?? ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? undefined : Number(e.target.value))
          }
          className="ui-table-filter-input"
        />
      )
    }

    case "number-range": {
      const range = (value as [number?, number?]) ?? [undefined, undefined]
      return (
        <div className="ui-table-filter-range">
          <Input
            type="number"
            size="xs"
            placeholder="Min"
            value={range[0] ?? ""}
            onChange={(e) =>
              onChange([
                e.target.value === "" ? undefined : Number(e.target.value),
                range[1],
              ])
            }
            className="ui-table-filter-range-input"
          />
          <span className="ui-table-filter-range-separator">–</span>
          <Input
            type="number"
            size="xs"
            placeholder="Max"
            value={range[1] ?? ""}
            onChange={(e) =>
              onChange([
                range[0],
                e.target.value === "" ? undefined : Number(e.target.value),
              ])
            }
            className="ui-table-filter-range-input"
          />
        </div>
      )
    }

    case "select": {
      return (
        <Select
          value={(value as string) ?? "__all__"}
          onValueChange={(v) => onChange(v === "__all__" ? undefined : v)}
        >
          <Select.Trigger size="xs" className="ui-table-filter-input">
            <Select.Value placeholder={filter.placeholder ?? "All"} />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="__all__">All</Select.Item>
            <Select.Separator />
            {filter.options?.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                <span className="flex items-center gap-2">
                  {option.dotVariant && <Dot variant={option.dotVariant} />}
                  {option.label}
                </span>
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      )
    }

    case "multi-select": {
      const selected = Array.isArray(value) ? (value as string[]) : []
      return (
        <MultiSelect
          size="xs"
          value={selected}
          onChange={(next) => onChange(next.length > 0 ? next : undefined)}
          options={(filter.options ?? []).map((option) => ({
            value: option.value,
            label: option.label,
            icon: option.dotVariant ? (
              <Dot variant={option.dotVariant} />
            ) : undefined,
          }))}
          placeholder={filter.placeholder ?? "Any"}
          className="ui-table-filter-input"
        />
      )
    }

    case "boolean": {
      return (
        <Select
          value={
            value === true ? "true" : value === false ? "false" : "__all__"
          }
          onValueChange={(v) => {
            if (v === "true") onChange(true)
            else if (v === "false") onChange(false)
            else onChange(v)
          }}
        >
          <Select.Trigger size="xs" className="ui-table-filter-input">
            <Select.Value placeholder="All" />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="__all__">All</Select.Item>
            <Select.Separator />
            <Select.Item value="true">Yes</Select.Item>
            <Select.Item value="false">No</Select.Item>
          </Select.Content>
        </Select>
      )
    }

    case "date": {
      const dateValue = value ? new Date(value as string) : undefined
      return (
        <DatePicker
          date={dateValue}
          setDate={(d) => onChange(d?.toISOString() ?? undefined)}
          renderTrigger={
            <Button
              variant="outline"
              size="xs"
              className="ui-table-filter-input"
              iconSrc="calendar"
              iconSize={12}
            >
              {dateValue
                ? format(dateValue, "PP", { locale: getDateFnsLocale() })
                : (filter.placeholder ?? "Pick date")}
            </Button>
          }
        />
      )
    }

    case "date-range": {
      const range = (value as [string?, string?]) ?? [undefined, undefined]
      const from = range[0] ? new Date(range[0]) : undefined
      const to = range[1] ? new Date(range[1]) : undefined
      return (
        <div className="ui-table-filter-range">
          <DatePicker
            date={from}
            setDate={(d) => onChange([d?.toISOString(), range[1]])}
            renderTrigger={
              <Button
                variant="outline"
                size="xs"
                className="ui-table-filter-input"
                iconSrc="calendar"
                iconSize={12}
              >
                {from
                  ? format(from, "PP", { locale: getDateFnsLocale() })
                  : "From"}
              </Button>
            }
          />
          <span className="ui-table-filter-range-separator">–</span>
          <DatePicker
            date={to}
            setDate={(d) => onChange([range[0], d?.toISOString()])}
            renderTrigger={
              <Button
                variant="outline"
                size="xs"
                className="ui-table-filter-input"
                iconSrc="calendar"
                iconSize={12}
              >
                {to ? format(to, "PP", { locale: getDateFnsLocale() }) : "To"}
              </Button>
            }
          />
        </div>
      )
    }

    case "date-filter": {
      return (
        <DateFilter
          value={value as DateFilterValue | undefined}
          onChange={onChange}
        />
      )
    }

    default: {
      return null
    }
  }
}

export default TableFilterButtonInput
